"use client"

import Link from "next/link"
import { ArrowRight, Wrench, Package, Settings } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { motion } from "motion/react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"

interface SearchItem {
  title: string
  href: string
  icon: LucideIcon
  description: string
}

const searches: SearchItem[] = [
  {
    title: "Affûtage",
    href: "/affutage",
    icon: Wrench,
    description:
      "L'affûtage de vos outils coupants pour une productivité maximale",
  },
  {
    title: "Outillage",
    href: "/outillage",
    icon: Package,
    description:
      "Des marques de qualité et de confiance pour travailler efficacement",
  },
  {
    title: "Maintenance",
    href: "/maintenance",
    icon: Settings,
    description:
      "Maintenance préventive et corrective de votre parc machines",
  },
]

export function ServicesSearch() {
  return (
    <section className="section-loose px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        {/* Section heading */}
        <div className="mb-12">
          <Badge
            variant="secondary"
            className="mb-4 text-xs uppercase tracking-[0.15em]"
          >
            {"Découvrir"}
          </Badge>
          <h2
            className="text-3xl font-bold tracking-tight md:text-4xl"
            style={{ textWrap: "balance" }}
          >
            {"VOUS RECHERCHEZ..."}
          </h2>
        </div>

        {/* Asymmetric card grid — first card spans 2 cols */}
        <motion.div
          className="grid grid-cols-1 gap-6 md:grid-cols-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          transition={{ staggerChildren: 0.1 }}
        >
          {searches.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={item.href}
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -3, transition: { duration: 0.2 } }}
                className={cn(index === 0 && "md:col-span-2")}
              >
              <Link
                href={item.href}
                className="group block"
              >
                <div
                  className={cn(
                    "rounded-[2rem] p-1.5",
                    "bg-foreground/[0.02] ring-1 ring-foreground/5",
                    "dark:bg-background/[0.06] dark:ring-foreground/10",
                    "transition-[box-shadow,ring-color] duration-300 ease-[var(--ease-premium)]",
                    "hover:shadow-[var(--shadow-md)] hover:ring-foreground/10",
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center gap-6 rounded-[calc(2rem-0.375rem)] bg-card p-6 md:p-8",
                      index === 0 ? "md:p-10" : ""
                    )}
                  >
                    {/* Icon */}
                    <span
                      className={cn(
                        "flex shrink-0 items-center justify-center rounded-2xl bg-primary/10",
                        index === 0 ? "size-16" : "size-14"
                      )}
                    >
                      <Icon
                        className={cn(
                          "text-primary",
                          index === 0 ? "size-8" : "size-7"
                        )}
                      />
                    </span>

                    {/* Text */}
                    <div className="min-w-0 flex-1">
                      <h3
                        className={cn(
                          "font-bold tracking-tight",
                          index === 0 ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"
                        )}
                      >
                        {item.title}
                      </h3>
                      <p className="mt-1 max-w-[65ch] text-sm text-muted-foreground md:text-base">
                        {item.description}
                      </p>
                    </div>

                    {/* Arrow */}
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 transition-[transform] duration-200 ease-[var(--ease-out-expo)] group-hover:translate-x-0.5">
                      <ArrowRight className="size-5 text-primary" />
                    </span>
                  </div>
                </div>
              </Link>
              </motion.div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}
